import { flexRender } from '@tanstack/react-table';
import { ColumnFiltersState } from '@tanstack/react-table';
import cn from 'classnames';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { TableHead, TableHeader, TableRow } from '../ui-kit/table';
import { DataGridColumn } from './data-grid-types';
import { ColumnFilter } from './data-grid-column-filter';

interface DataGridTableHeadProps<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  table: any;
  columns: DataGridColumn<T>[];
  showColumnFilters: boolean;
  enableFiltering: boolean;
  filterMode: 'inline' | 'popover';
  manualFiltering: boolean;
  columnFilters: ColumnFiltersState;
  commitFilterValue: (columnId: string, val: string, commit?: boolean) => void;
  headerBg: string;
  themeBorder: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getColour: (element: any) => string;
}

export function DataGridTableHead<T>({
  table,
  showColumnFilters,
  enableFiltering,
  filterMode,
  manualFiltering,
  columnFilters,
  commitFilterValue,
  headerBg,
  themeBorder,
  getColour
}: DataGridTableHeadProps<T>) {
  const themeText = getColour('normalText');

  return (
    <TableHead
      className="sticky top-0 z-20"
      style={{ background: headerBg, borderBottom: `1px solid ${themeBorder}` }}
    >
      {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        table.getHeaderGroups().map((headerGroup: any) => (
          <TableRow
            key={headerGroup.id}
            style={{
              color: themeText,
              backgroundColor: 'transparent',
              borderBottom: `1px solid ${themeBorder}`
            }}
          >
            {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              headerGroup.headers.map((header: any) => {
                const columnDef = header.column.columnDef as DataGridColumn<T>;
                const hasInlineFilter =
                  showColumnFilters &&
                  enableFiltering &&
                  header.column.getCanFilter() &&
                  columnDef.enableFiltering !== false &&
                  filterMode === 'inline';

                const currentFilterValue =
                  (columnFilters.find(f => f.id === header.column.id)
                    ?.value as string) ?? '';

                const isSorted = header.column.getIsSorted();
                const hasActiveFilter = !!currentFilterValue;

                const canSort = header.column.getCanSort();
                const canFilter =
                  filterMode === 'popover' &&
                  enableFiltering &&
                  header.column.getCanFilter() &&
                  columnDef.enableFiltering !== false;

                return (
                  <TableHeader
                    key={header.id}
                    className="group relative whitespace-nowrap !px-3 !py-0 align-middle transition-colors hover:bg-black/[0.02] dark:hover:bg-white/[0.02]"
                    style={{
                      verticalAlign: 'middle',
                      width:
                        header.getSize() !== 150 ? header.getSize() : undefined,
                      minWidth: header.column.columnDef.minSize,
                      maxWidth: header.column.columnDef.maxSize,
                      borderColor: themeBorder,
                      height: '36px',
                      // Every <th> gets its own opaque fill. Setting
                      // `background` only on the sticky <thead> is
                      // unreliable — browsers paint scrolled rows
                      // through <th> cells that lack their own
                      // background, so the Name column (and anything
                      // else without an explicit fill) appeared
                      // transparent as rows scrolled beneath it.
                      // ``actions`` overrides this below to add sticky
                      // positioning + shadow.
                      background: headerBg,
                      ...(header.column.id === 'actions' && {
                        position: 'sticky',
                        right: 0,
                        zIndex: 21,
                        background: headerBg,
                        boxShadow: '-4px 0 8px -4px rgba(0,0,0,0.08)'
                      })
                    }}
                  >
                    {!['select', 'expand', 'actions'].includes(
                      header.column.id
                    ) && (
                      <span
                        aria-hidden
                        className="pointer-events-none absolute right-0 top-[25%] bottom-[25%] w-px opacity-30"
                        style={{ backgroundColor: themeBorder }}
                      />
                    )}

                    {header.isPlaceholder ? null : (
                      <div
                        className={cn(
                          'flex flex-col',
                          hasInlineFilter ? 'gap-1.5' : ''
                        )}
                      >
                        {header.column.id === 'select' ||
                        header.column.id === 'expand' ? (
                          flexRender(
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            (header.column.columnDef as any).header,
                            header.getContext()
                          )
                        ) : (
                          <div
                            className={cn(
                              'flex items-center w-full gap-1',
                              header.column.id === 'actions' && 'justify-center'
                            )}
                          >
                            <div
                              className={cn(
                                'flex-1 min-w-0',
                                canSort && 'cursor-pointer select-none'
                              )}
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              <span
                                className="block truncate text-[11px] font-semibold uppercase tracking-wide"
                                style={{ color: getColour('inactiveText') }}
                              >
                                {(() => {
                                  const rawHeader =
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    (header.column.columnDef as any).header ??
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    (header.column.columnDef as any)
                                      .headerName ??
                                    header.column.id;
                                  return flexRender(
                                    rawHeader,
                                    header.getContext()
                                  );
                                })()}
                              </span>
                            </div>

                            {(canSort || canFilter) && (
                              <div className="flex items-center gap-0.5 flex-shrink-0">
                                {canSort && (
                                  <button
                                    className="inline-flex items-center p-0.5 rounded-md cursor-pointer transition-colors hover:bg-black/[0.06] dark:hover:bg-white/[0.06]"
                                    onClick={e => {
                                      e.stopPropagation();
                                      header.column.getToggleSortingHandler()?.(
                                        e
                                      );
                                    }}
                                    style={{
                                      color: isSorted
                                        ? getColour('activeText')
                                        : getColour('inactiveText'),
                                      opacity: isSorted ? 1 : 0.4
                                    }}
                                  >
                                    {isSorted === 'asc' ? (
                                      <ChevronUp className="w-3.5 h-3.5" />
                                    ) : isSorted === 'desc' ? (
                                      <ChevronDown className="w-3.5 h-3.5" />
                                    ) : (
                                      <ChevronsUpDown className="w-3.5 h-3.5" />
                                    )}
                                  </button>
                                )}

                                {canFilter && (
                                  <ColumnFilter
                                    column={header.column}
                                    columnDef={columnDef}
                                    manualFiltering={manualFiltering}
                                    value={currentFilterValue}
                                    onValueChange={(val, commit) =>
                                      commitFilterValue(
                                        header.column.id,
                                        val,
                                        !!commit
                                      )
                                    }
                                    showInline={false}
                                    buttonOpacity={hasActiveFilter ? 1 : 0.4}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {hasInlineFilter && (
                          <div className="pt-1">
                            <ColumnFilter
                              column={header.column}
                              columnDef={columnDef}
                              manualFiltering={manualFiltering}
                              value={currentFilterValue}
                              onValueChange={(val, commit) =>
                                commitFilterValue(
                                  header.column.id,
                                  val,
                                  !!commit
                                )
                              }
                              showInline
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </TableHeader>
                );
              })
            }
          </TableRow>
        ))
      }
    </TableHead>
  );
}
